import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useNotificationSender } from '@app/hooks/useNotificationSender';
import { AppMenuItem } from '@type/layout';
import * as _ from 'lodash-es';
import { Ripple } from 'primereact/ripple';
import { classNames } from 'primereact/utils';
import { Link, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

const AppMenuitemLarge = ({ item, parentKey, index, root, activeMenus, setActiveMenus }: any) => {
  const { pathname } = useLocation();
  const key = parentKey ? `${parentKey}-${index}` : String(index);
  const { setLoadingConfirmedMessageList, setLoadingUnreadMessage } = useNotificationSender();

  if (item.separator && item.visible !== false) {
    return <li className="menu-separator" />;
  }

  const isActivePath = (to?: string): boolean => {
    if (!to) return false;
    // Remove query params from pathname and to for comparison
    const cleanPath = pathname.split('?')[0];
    const cleanTo = to.split('?')[0];
    return cleanPath === cleanTo || cleanPath.startsWith(cleanTo + '/');
  };

  const isActiveItem = (menuItem: any): boolean => {
    if (isActivePath(menuItem.to)) return true;
    if (menuItem.items && menuItem.items.length > 0) {
      return menuItem.items.some((child: any) => isActiveItem(child));
    }
    return false;
  };

  const isActiveRoute = isActiveItem(item);

  // Highlight as active if current route matches (or any child matches)
  const active = useMemo(() => {
    return isActiveRoute;
  }, [isActiveRoute]);

  const itemClick = (event: React.MouseEvent) => {
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    // If the item has a 'to' property, it means it's a navigational link, so we set loading state for notifications
    if (item.to) {
      // Set loading state for notifications when navigating to a new route
      setLoadingConfirmedMessageList?.(true);
      setLoadingUnreadMessage?.(true);
    }

    if (item.command) {
      item.command({ originalEvent: event, item });
    }

    if (item.items) {
      // Toggle current submenu only; when closing a parent, close all its descendants too.
      setActiveMenus((prevMenus: string[]) => {
        const isCurrentExpanded = prevMenus.some((menuKey) => menuKey === key || menuKey.startsWith(`${key}-`));

        if (isCurrentExpanded) {
          return prevMenus.filter((menuKey) => menuKey !== key && !menuKey.startsWith(`${key}-`));
        }

        return [...prevMenus, key];
      });
    } else {
      // Clicking a leaf menu item should close all submenus
      setActiveMenus([]);
    }
  };

  const submenuRef = useRef(null);

  const subMenu = item.items && item.visible !== false && (
    <CSSTransition
      timeout={{ enter: 1000, exit: 450 }}
      classNames="layout-submenu"
      in={root ? true : activeMenus.some((menuKey: string) => menuKey === key || menuKey.startsWith(`${key}-`))}
      key={item.label}
      nodeRef={submenuRef}
      onEntered={() => {
        // Remove auto-focus from submenu items
        setTimeout(() => {
          if (submenuRef.current) {
            const focused = (submenuRef.current as HTMLElement).querySelector(
              '.p-menuitem-content:focus, .p-menuitem-link:focus, [data-p-focused="true"]'
            ) as HTMLElement;
            if (focused) focused.blur();
          }
        }, 10);
      }}
    >
      <ul ref={submenuRef}>
        {item.items.map((child: any, i: any) => (
          <AppMenuitemLarge
            key={child.label}
            item={child}
            parentKey={key}
            index={i}
            root={false}
            activeMenus={activeMenus}
            setActiveMenus={setActiveMenus}
          />
        ))}
      </ul>
    </CSSTransition>
  );

  return (
    <li
      className={classNames({
        'layout-root-menuitem': root,
        'active-menuitem': active,
      })}
    >
      {root && item.visible !== false && <div className="layout-menuitem-root-text">{item.label}</div>}

      {(!item.to || item.items) && item.visible !== false ? (
        <a
          href={item.url}
          onClick={itemClick}
          className={classNames(item.class, 'p-ripple no-underline', {
            'active-route': isActiveRoute,
          })}
          target={item.target}
          tabIndex={0}
        >
          {item.iconImg && <img src={item.iconImg} alt={item.label} className="layout-menuitem-icon" />}
          <span className="layout-menuitem-text">{item.label}</span>
          {item.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler" />}
          <Ripple />
        </a>
      ) : null}

      {item.to && !item.items && item.visible !== false ? (
        <Link
          to={item.to}
          target={item.target}
          onClick={itemClick}
          className={classNames(item.class, 'p-ripple no-underline', {
            'active-route': isActiveRoute,
          })}
          tabIndex={0}
        >
          {item.iconImg ? (
            <img src={item.iconImg} alt={item.label} className="layout-menuitem-icon" />
          ) : (
            <i className={classNames('layout-menuitem-icon', item.icon)} />
          )}
          <span className="layout-menuitem-text">{item.label}</span>
          {item.items && <i className="pi pi-fw pi-angle-down layout-submenu-toggler" />}
          <Ripple />
        </Link>
      ) : null}

      {subMenu}
    </li>
  );
};

const AppMenuLarge = ({ model, isVisible = true }: { model: AppMenuItem[]; isVisible?: boolean }) => {
  const { pathname } = useLocation();
  const [activeMenus, setActiveMenus] = useState<string[]>([]);

  useEffect(() => {
    const cleanPath = pathname.split('?')[0];

    const isActivePath = (to?: string): boolean => {
      if (!to) return false;
      const cleanTo = to.split('?')[0];
      return cleanPath === cleanTo || cleanPath.startsWith(`${cleanTo}/`);
    };

    const findActiveMenuKey = (items: AppMenuItem[], parentKey = ''): string => {
      let activeKey = '';
      _.find(items, (item, index) => {
        const key = parentKey ? `${parentKey}-${index}` : String(index);
        if (isActivePath(item.to)) {
          activeKey = key;
          return true;
        }
        if (item.items?.length) {
          const childKey = findActiveMenuKey(item.items, key);
          if (childKey) {
            activeKey = childKey;
            return true;
          }
        }
        return false;
      });
      return activeKey;
    };

    const activeMenuKey = findActiveMenuKey(model);
    setActiveMenus(activeMenuKey ? [activeMenuKey] : []);
  }, [model, pathname]);

  return (
    <div className="layout-menu-large" style={{ opacity: isVisible ? 1 : 0, transition: 'opacity 0.3s ease-in-out' }}>
      <ul className="layout-menu">
        {model.map((item, i) => (
          <AppMenuitemLarge
            key={`menu-item-${i}`}
            item={item}
            root
            index={i}
            activeMenus={activeMenus}
            setActiveMenus={setActiveMenus}
          />
        ))}
      </ul>
      {import.meta.env.VITE_ENV === 'LOCAL_DEV' && (
        <div className="version pl-5 pb-4 fw-700 text-color-primary">Version: {import.meta.env.VITE_VER}</div>
      )}
    </div>
  );
};
export default memo(AppMenuLarge);
